import { useState } from 'react';
import { useReportMonitors } from '@/monitoring/useReportMonitors';
import {
  useMediaQuery,
  Text,
  useTheme,
  Tab,
  Tabs,
  styled,
  Box,
} from '@/theme/index';
import { UnclaimedMoneyResultsGrid } from './UnclaimedMoneyResultsGrid/UnclaimedMoneyResultsGrid';
import { UnclaimedMoneyResultsInformation } from './UnclaimedMoneyResultsInformation';
import { MonitorReportButton } from '../components/MonitorReportButton';
import { MonitorSuccessModal } from '../components/MonitorSuccessModal';
import {
  UnclaimedMoneyResultsComponentProps,
  UnclaimedMoneyReportItem,
} from '../interfaces';

const StyledText = styled(Text)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 400,
  marginTop: theme.spacing(2),
}));

const MonitorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  padding: '6px 18px',
  marginBottom: '16px',
  borderRadius: '8px',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: '#02236D',
    marginTop: '18px',
    marginBottom: '0',
  },
}));

export function UnclaimedMoneyResultsMultiStates({
  firstName,
  lastName,
  noResultsImage,
  unclaimedMoneyReports,
  loadingBbqObject,
  bodyImage,
  states,
}: UnclaimedMoneyResultsComponentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentReport, setCurrentReport] =
    useState<UnclaimedMoneyReportItem | null>(
      unclaimedMoneyReports[currentTabIndex] || null
    );

  const { reportIsMonitored, toggleReportIsMonitored } = useReportMonitors(
    currentReport?.permalink
  );

  const [totalResults, setTotalResults] = useState(
    currentReport ? currentReport.totalResults : 0
  );
  const [portionResults, setPortionResults] = useState(
    currentReport ? currentReport.totalResults : 0
  );
  const handleTabChange = (event: React.SyntheticEvent, tabIndex: number) => {
    setCurrentTabIndex(tabIndex);
    setCurrentReport(unclaimedMoneyReports[tabIndex] || null);
    setTotalResults(unclaimedMoneyReports[tabIndex].totalResults || 0);
    setPortionResults(unclaimedMoneyReports[tabIndex].totalResults || 0);
  };

  const handleSetIsMonitored = () => {
    if (!reportIsMonitored) {
      setIsModalOpen(true);
    }
    toggleReportIsMonitored();
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      {!isMobile && (
        <Text variant="body1" fontSize={13}>
          Unclaimed property with the exact name and state you searched will
          appear first, similar matches will follow. We recommend reviewing the
          entire list since property often ends up unclaimed due to spelling
          errors.
        </Text>
      )}

      {!loadingBbqObject && (
        <>
          {currentReport && currentReport.totalResults > 0 ? (
            <StyledText variant="h1">
              Showing <strong>{portionResults}</strong> of{' '}
              <strong>{totalResults}</strong> Results for{' '}
              <strong>{firstName}</strong> <strong>{lastName}</strong>
            </StyledText>
          ) : (
            <StyledText variant="h1">
              <strong>0</strong> results for <strong>{firstName}</strong>{' '}
              <strong>{lastName}</strong>
            </StyledText>
          )}

          <Tabs
            value={currentTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable unclaimed money states tabs"
            sx={{ marginBottom: '20px' }}
          >
            {states?.map((state) => (
              <Tab label={state} />
            ))}
          </Tabs>

          {currentReport ? (
            <MonitorContainer>
              <Text fontSize={14}>Stay updated on this search</Text>
              <MonitorReportButton
                handleSetIsMonitored={handleSetIsMonitored}
                isMonitored={reportIsMonitored}
              />
            </MonitorContainer>
          ) : (
            <UnclaimedMoneyResultsInformation
              informationImage={noResultsImage}
              title="Sorry, your results are not ready yet."
              subTitle="We are still processing your report. Please check back later or try refreshing the page."
              noResults
              reloadAvailable
            />
          )}

          {currentReport && currentReport.totalResults > 0 && (
            <UnclaimedMoneyResultsGrid
              unclaimedMoneyRows={currentReport.unclaimedMoneyRows}
            />
          )}
          {currentReport && currentReport?.totalResults === 0 && (
            <UnclaimedMoneyResultsInformation
              informationImage={noResultsImage}
              title="Sorry, no matches found at this time."
              subTitle="New unclaimed money gets added every day. Check back tomorrow"
              noResults
            />
          )}
        </>
      )}

      {currentReport && (
        <MonitorSuccessModal
          isOpen={isModalOpen}
          onClose={closeModal}
          firstName={firstName}
          lastName={lastName}
          bodyImage={bodyImage}
          states={currentReport.state}
        />
      )}
    </>
  );
}
